import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SupplierFinderForm from '../../components/finance/SupplierFinderForm';
import SupplierResults from '../../components/finance/SupplierResults';
import { SupplierFinderResponse } from '../../services/supplierFinderService';
import { toast } from 'react-hot-toast';

const SupplierFinder: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State for supplier results
  const [results, setResults] = useState<SupplierFinderResponse>({ suppliers: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);
  
  // Set document title for SEO
  useEffect(() => {
    document.title = 'Trade Supplier Finder | Explanatum';
  }, []);
  
  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }
  
  return (
    <section className="py-12 lg:py-20 relative overflow-hidden">
      {/* Background with subtle gradient */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-20 bg-gradient-to-br from-green-50 to-teal-50"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Page header */}
        <div className="max-w-3xl mx-auto mb-12 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Trade Supplier Finder
          </h1>
          <p className="text-lg text-gray-600">
            Find licensed trade suppliers for products and services in your preferred location
          </p>
        </div>
        
        {/* Two-column layout on larger screens */}
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Form column */}
          <div className="lg:col-span-5">
            <SupplierFinderForm 
              onResults={setResults}
              onLoading={setIsLoading}
              onError={setError}
            />
            
            {/* Tips for searching */}
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mt-6">
              <h3 className="text-lg font-medium text-blue-800 mb-2">Search Tips</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Be specific about the product or service you need</li>
                <li>• Include specific regions or cities for more localized results</li>
                <li>• For licensed merchandise, include specific brand names</li>
                <li>• Consider including keywords like "wholesale" or "manufacturer"</li>
              </ul>
            </div>
          </div>
          
          {/* Results column */}
          <div className="lg:col-span-7">
            <SupplierResults 
              suppliers={results.suppliers}
              isLoading={isLoading}
              error={error}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SupplierFinder;
